import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InterfaceLanguageOptionDto } from '@asset-metrix/portal-gateway-client-js';
import i18n from 'i18next';

import {
  type ColorScheme,
  discardColorSchemePreview,
  useColorScheme,
} from '../../theme/usePortalThemeBase';
import {
  getConfiguredLanguageCodes,
  getConfiguredLanguageOptions,
  isSupportedLanguage,
  resolvePreferredLanguage,
  setPreferredLanguage,
} from '../../theme/userLanguagePreference';
import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { Input } from '../input/Input';
import { Modal } from '../modal/Modal';
import { RadioGroup } from '../radio/RadioGroup';
import { SegmentedControl } from '../segmentedControl/SegmentedControl';
import { SingleSelect } from '../selects/SingleSelect';

import styles from './user-settings-modal.module.scss';

export type UserSettingsTenantDefaults = {
  /** Tenant default when the user has no stored color scheme preference. */
  colorScheme?: ColorScheme;
  /** Tenant locale hint (e.g. login-info `localeMessages`). */
  language?: string;
  /** Enabled interface languages from login-info when tenant i18n is configured. */
  languages?: InterfaceLanguageOptionDto[];
};

const LOGIN_TAB = 'login';
const PORTAL_TAB = 'portal';

export type UserSettingsModalProps = {
  tenantDefaults?: UserSettingsTenantDefaults;
  onChangePassword?: () => void;
  onClose: () => void;
  onLanguageSaved?: (language: string) => void;
  opened: boolean;
  userCanSwitchTheme?: boolean;
  userEmail?: string;
  withinPortal?: boolean;
};

function getLanguageCode(language: string | undefined) {
  return typeof language === 'string' ? language.split('-')[0] : undefined;
}

function getAppliedLanguageCode(
  defaultLanguage?: string,
  configuredLanguages?: InterfaceLanguageOptionDto[]
) {
  const hasConfiguredLanguages =
    configuredLanguages != null && configuredLanguages.length > 0;

  if (!hasConfiguredLanguages) {
    return (
      getLanguageCode(i18n.language) ??
      getLanguageCode(i18n.resolvedLanguage) ??
      resolvePreferredLanguage(defaultLanguage)
    );
  }

  const supportedLanguages = getConfiguredLanguageCodes(
    configuredLanguages,
    defaultLanguage
  );

  const requestedLanguage = getLanguageCode(i18n.language);
  if (
    requestedLanguage &&
    isSupportedLanguage(requestedLanguage, supportedLanguages)
  ) {
    return requestedLanguage;
  }

  const resolvedLanguage = getLanguageCode(i18n.resolvedLanguage);
  if (
    resolvedLanguage &&
    isSupportedLanguage(resolvedLanguage, supportedLanguages)
  ) {
    return resolvedLanguage;
  }

  return resolvePreferredLanguage(defaultLanguage, supportedLanguages);
}

export function UserSettingsModal({
  tenantDefaults,
  onChangePassword,
  onClose,
  onLanguageSaved,
  opened,
  userCanSwitchTheme = false,
  userEmail,
  withinPortal = true,
}: UserSettingsModalProps) {
  const defaultColorScheme = tenantDefaults?.colorScheme;
  const defaultLanguage = tenantDefaults?.language;
  const configuredLanguages = tenantDefaults?.languages;
  const { t } = useTranslation();
  const colorModeGroupName = useId();
  const [colorScheme, setColorScheme] = useColorScheme(defaultColorScheme);
  const [activeTab, setActiveTab] = useState(LOGIN_TAB);
  const [draftLanguage, setDraftLanguage] = useState(() =>
    getAppliedLanguageCode(defaultLanguage, configuredLanguages)
  );
  const [draftColorScheme, setDraftColorScheme] =
    useState<ColorScheme>(colorScheme);
  const colorSchemeRef = useRef(colorScheme);
  const languageSnapshotRef = useRef(draftLanguage);

  colorSchemeRef.current = colorScheme;

  useEffect(() => {
    if (!opened) {
      return;
    }

    const currentLanguage = getAppliedLanguageCode(
      defaultLanguage,
      configuredLanguages
    );
    const currentColorScheme = colorSchemeRef.current;
    languageSnapshotRef.current = currentLanguage;
    setDraftLanguage(currentLanguage);
    setDraftColorScheme(currentColorScheme);
    setActiveTab(LOGIN_TAB);
  }, [configuredLanguages, defaultLanguage, opened]);

  const restoreSnapshot = () => {
    i18n.changeLanguage(languageSnapshotRef.current);
    discardColorSchemePreview();
  };

  const handleClose = () => {
    restoreSnapshot();
    onClose();
  };

  const handleSave = () => {
    const previousLanguage = languageSnapshotRef.current;
    setPreferredLanguage(draftLanguage);
    if (draftLanguage !== previousLanguage) {
      onLanguageSaved?.(draftLanguage);
    }
    if (userCanSwitchTheme) {
      setColorScheme(draftColorScheme, { persist: true });
    }
    onClose();
  };

  const handleLanguageChange = (language: string | null) => {
    if (language == null) {
      return;
    }

    setDraftLanguage(language);
    i18n.changeLanguage(language);
  };

  const handleColorSchemeChange = (value: string) => {
    const nextColorScheme: ColorScheme = value === 'dark' ? 'dark' : 'light';
    setDraftColorScheme(nextColorScheme);
    setColorScheme(nextColorScheme, { persist: false });
  };

  const handleChangePassword = () => {
    restoreSnapshot();
    onClose();
    onChangePassword?.();
  };

  const languageOptions = getConfiguredLanguageOptions(
    configuredLanguages ?? [],
    defaultLanguage
  );
  const showLanguageSelect = languageOptions.length >= 2;
  const showPortalSettings = showLanguageSelect || userCanSwitchTheme;

  const tabOptions = [
    {
      label: t('app.userSettings.loginInformation'),
      value: LOGIN_TAB,
    },
    ...(showPortalSettings
      ? [
          {
            label: t('app.userSettings.portalSettings'),
            value: PORTAL_TAB,
          },
        ]
      : []),
  ];

  return (
    <Modal
      actions={
        <>
          <Button
            data-testid="user-settings-modal__cancel"
            icon={<GoogleFontIcon name="close" />}
            onClick={handleClose}
            variant="secondary"
          >
            {t('app.userSettings.cancel')}
          </Button>
          <Button
            data-testid="user-settings-modal__save"
            icon={<GoogleFontIcon name="check" />}
            onClick={handleSave}
          >
            {t('app.userSettings.saveAndExit')}
          </Button>
        </>
      }
      modalType="action"
      onClose={handleClose}
      opened={opened}
      size={560}
      title={t('app.userSettings.title')}
      withCloseButton
      withinPortal={withinPortal}
    >
      <div className={styles['content']} data-testid="user-settings-modal">
        {showPortalSettings && (
          <SegmentedControl
            onChange={setActiveTab}
            options={tabOptions}
            value={activeTab}
          />
        )}
        {activeTab === LOGIN_TAB && (
          <div className={styles['panel']}>
            <p className={styles['description']}>
              {t('app.userSettings.loginDescription')}
            </p>
            <Input
              data-testid="user-settings-modal__email"
              label={t('app.userSettings.email')}
              readOnly
              value={userEmail ?? ''}
            />
            {onChangePassword && (
              <Button
                className={styles['password-action']}
                data-testid="user-settings-modal__change-password"
                onClick={handleChangePassword}
                variant="secondary"
              >
                {t('app.changePassword')}
              </Button>
            )}
          </div>
        )}
        {showPortalSettings && activeTab === PORTAL_TAB && (
          <div className={styles['panel']}>
            {showLanguageSelect && (
              <div data-testid="user-settings-modal__language">
                <SingleSelect
                  clearable={false}
                  data={languageOptions}
                  label={t('app.userSettings.language')}
                  onChange={handleLanguageChange}
                  value={draftLanguage}
                  withinPortal={withinPortal}
                />
              </div>
            )}
            {userCanSwitchTheme && (
              <div data-testid="user-settings-modal__color-mode">
                <RadioGroup
                  className={styles['color-mode-radios']}
                  label={t('app.userSettings.colorMode')}
                  name={colorModeGroupName}
                  onChange={handleColorSchemeChange}
                  options={[
                    {
                      label: t('app.userSettings.lightMode'),
                      value: 'light',
                    },
                    {
                      label: t('app.userSettings.darkMode'),
                      value: 'dark',
                    },
                  ]}
                  value={draftColorScheme}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
